import {useI18n} from '@/i18n'
import type {PreviewScopeResponse} from '@/types/certification'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Skeleton} from '@/components/ui/skeleton'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'

export interface ScopePreviewPanelProps {
    data: PreviewScopeResponse | undefined
    error: Error | null
    inFlight: boolean
    idle: boolean
    onRetry: () => void
}

function FrequencyDisclaimer() {
    const i18n = useI18n()
    return (
        <MutedText size="xs" block data-testid="preview-frequency-disclaimer">
            {i18n.certificationPolicies.wizard.previewFrequencyDisclaimer}
        </MutedText>
    )
}

/**
 * Always-visible inline scope-preview panel (replaces the legacy modal dialog).
 * Renders every spec §7.3 state: no-query, in-flight skeleton (stable height),
 * data (counts + sample table), zero-match, error (+ retry), sampled footer, and
 * the expression-mode tooLarge state. The frequency disclaimer is shown in every
 * state that could mislead (counts / no-query — spec §3.4).
 */
export function ScopePreviewPanel({data, error, inFlight, idle, onRetry}: ScopePreviewPanelProps) {
    const i18n = useI18n()

    if (inFlight) {
        return (
            <Stack gap="sm" data-testid="scope-preview-panel">
                <Stack gap="sm" data-testid="preview-loading">
                    <Skeleton className="h-6"/>
                    <Skeleton className="h-6"/>
                    <Skeleton className="h-6"/>
                </Stack>
                <FrequencyDisclaimer/>
            </Stack>
        )
    }

    if (error) {
        return (
            <Stack gap="sm" data-testid="scope-preview-panel">
                <InfoBanner variant="destructive" data-testid="preview-error-banner">
                    <InlineRow justify="between" align="center">
                        <span>{i18n.certificationPolicies.wizard.previewError}</span>
                        <Button type="button" variant="outline" size="sm" onClick={onRetry} data-testid="btn-preview-retry">
                            {i18n.certificationPolicies.wizard.previewRetry}
                        </Button>
                    </InlineRow>
                </InfoBanner>
                <FrequencyDisclaimer/>
            </Stack>
        )
    }

    if (idle || data === undefined) {
        return (
            <Stack gap="sm" data-testid="scope-preview-panel">
                <InfoBanner variant="info" data-testid="preview-no-query">
                    {i18n.certificationPolicies.wizard.previewNoQuery}
                </InfoBanner>
                <FrequencyDisclaimer/>
            </Stack>
        )
    }

    if (data.tooLarge === true) {
        return (
            <Stack gap="sm" data-testid="scope-preview-panel">
                <InfoBanner variant="warning" data-testid="preview-too-large">
                    {i18n.certificationPolicies.wizard.previewTooLarge}
                </InfoBanner>
                <FrequencyDisclaimer/>
            </Stack>
        )
    }

    const matched = data.matched ?? 0
    const total = data.totalEvaluated ?? 0
    const items = data.items ?? []
    const sampled = items.length < matched

    if (matched === 0) {
        return (
            <Stack gap="sm" data-testid="scope-preview-panel">
                <InfoBanner variant="warning" data-testid="preview-zero-match-warning">
                    {i18n.certificationPolicies.wizard.previewZeroMatch}
                </InfoBanner>
                <FrequencyDisclaimer/>
            </Stack>
        )
    }

    return (
        <Stack gap="sm" data-testid="scope-preview-panel">
            <Strong data-testid="preview-in-scope">
                {i18n.certificationPolicies.wizard.previewInScope({matched, total})}
            </Strong>
            <ScrollableBox maxHeight="md" data-testid="table-preview-results">
                <Table>
                    <TableHeader sticky>
                        <TableRow>
                            <TableHead data-testid="col-preview-identity">{i18n.certificationPolicies.wizard.previewColIdentity}</TableHead>
                            <TableHead data-testid="col-preview-profile">{i18n.certificationPolicies.wizard.previewColProfile}</TableHead>
                            <TableHead data-testid="col-preview-resource">{i18n.certificationPolicies.wizard.previewColResource}</TableHead>
                            <TableHead data-testid="col-preview-risk-level">{i18n.certificationPolicies.wizard.previewColRiskLevel}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item, index) => (
                            <TableRow key={`${item.identityId}-${item.profileId}-${index}`} data-readonly>
                                <TableCell className="font-medium">{item.identityDisplayName}</TableCell>
                                <TableCell>
                                    <Stack gap="xs">
                                        <span>{item.profileCode}</span>
                                        <MutedText size="xs">{item.profileName}</MutedText>
                                    </Stack>
                                </TableCell>
                                <TableCell>{item.resourceDisplayName}</TableCell>
                                <TableCell>
                                    <Badge variant="outline">{item.riskLevel}</Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ScrollableBox>
            {sampled && (
                <MutedText size="xs" data-testid="preview-sample-caption">
                    {i18n.certificationPolicies.wizard.previewSampleCaption({count: items.length, matched})}
                </MutedText>
            )}
            <FrequencyDisclaimer/>
        </Stack>
    )
}
